import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Play, X, Volume2, VolumeX, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';

const isDirectVideo = (url) => {
  if (!url) return false;
  const lower = url.toLowerCase();
  return lower.includes('.mp4') || lower.includes('.mov') || lower.includes('.webm') || lower.includes('customer-assets') || lower.includes('/api/static/');
};

export const VideoModal = ({ isOpen, onClose, videoId, videoUrl, title }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef(null);

  const directUrl = isDirectVideo(videoUrl) ? videoUrl : (isDirectVideo(videoId) ? videoId : null);
  const ytId = !directUrl ? (videoId || '') : '';

  const handleOpen = (open) => {
    if (!open) {
      onClose();
      setIsLoading(true);
      setHasError(false);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpen}>
      <DialogContent className="max-w-4xl w-full p-0 bg-card border-border overflow-hidden" data-testid="video-modal">
        <div className="relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
            data-testid="video-modal-close"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          {title && (
            <div className="p-4 bg-gradient-to-r from-primary/20 to-secondary/20">
              <h3 className="text-xl font-serif text-white" data-testid="video-modal-title">{title}</h3>
            </div>
          )}

          <div className="relative aspect-video bg-black">
            {isLoading && !hasError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-card z-10">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-muted-foreground text-sm mt-3">Loading video...</p>
              </div>
            )}

            {hasError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-card z-10" data-testid="video-error">
                <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
                <p className="text-white text-lg font-medium mb-2">Video failed to load</p>
                <p className="text-muted-foreground text-sm mb-4">The video may be temporarily unavailable</p>
                <a
                  href={directUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-sm px-4 py-2"
                  data-testid="video-direct-link"
                >
                  Open Video Directly
                </a>
              </div>
            )}

            {directUrl ? (
              <>
                <video
                  ref={videoRef}
                  className="w-full h-full object-contain"
                  controls
                  autoPlay
                  playsInline
                  preload="auto"
                  onLoadedData={() => setIsLoading(false)}
                  onCanPlay={() => setIsLoading(false)}
                  onError={() => { setIsLoading(false); setHasError(true); }}
                  data-testid="video-player"
                >
                  {directUrl && <source src={directUrl} type="video/mp4" />}
                </video>
                {!hasError && (
                  <button
                    onClick={toggleMute}
                    className="absolute bottom-4 right-4 z-20 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                  >
                    {isMuted ? <VolumeX className="w-5 h-5 text-white" /> : <Volume2 className="w-5 h-5 text-white" />}
                  </button>
                )}
              </>
            ) : (
              <iframe
                src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`}
                title={title || "Video"}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                onLoad={() => setIsLoading(false)}
                data-testid="video-iframe"
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const VideoCard = ({ video, onClick, index = 0 }) => {
  const isDirect = isDirectVideo(video.youtube_url);
  const videoRef = useRef(null);
  const [hasThumb, setHasThumb] = useState(true);

  const getThumbnail = () => {
    if (video.thumbnail && !video.thumbnail.includes('youtube.com/vi/http') && video.thumbnail !== '') {
      return video.thumbnail;
    }
    if (video.youtube_id && !isDirectVideo(video.youtube_id)) {
      return `https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`;
    }
    return null;
  };

  const thumbnail = getThumbnail();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group cursor-pointer rounded-xl overflow-hidden bg-card border border-border/50 hover:border-primary/30 transition-all duration-300"
      onClick={() => onClick(video)}
      data-testid={`video-card-${video.id}`}
    >
      {/* Thumbnail Area - Clean, no text overlay */}
      <div className="relative aspect-video overflow-hidden bg-black">
        {isDirect && video.youtube_url ? (
          <video
            ref={videoRef}
            src={video.youtube_url}
            className="w-full h-full object-cover"
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={(e) => { e.target.currentTime = 1; }}
          />
        ) : thumbnail && hasThumb ? (
          <img
            src={thumbnail}
            alt={video.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={() => setHasThumb(false)}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
            <Play className="w-16 h-16 text-white/30" />
          </div>
        )}

        {/* Play button overlay - centered */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <motion.div
            whileHover={{ scale: 1.1 }}
            className="w-16 h-16 rounded-full bg-cta flex items-center justify-center shadow-lg shadow-cta/30"
          >
            <Play className="w-8 h-8 text-background ml-1" fill="currentColor" />
          </motion.div>
        </div>
      </div>

      {/* Text Area - Below the thumbnail, clean separation */}
      <div className="p-4">
        <h3 className="text-base font-semibold text-white line-clamp-2 mb-1" data-testid="video-card-title">
          {video.title}
        </h3>
        {video.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {video.description}
          </p>
        )}
      </div>
    </motion.div>
  );
};
